import { defineStore } from 'pinia'
import api from '@/helpers/axiosConfig'
import useErrorStore from './error'
import useCampaignStore from './campaign'
import axios from 'axios'


export default defineStore('receipt', {
  state: () => ({
    receipt: {
      receiptImage: null
    },
    customerToken: '',
    receiptToken: '',
    referenceNumber: '',
    retailerForm: {},
    postbillUrl: 'https://receipt.justsnap.eu/postBill',
    isLoading: false,
  }),
  getters: {
  },
  actions: {
    setReceiptImage(image) {
      this.receipt.receiptImage = image
    },
    async refreshTempId(routeQueries = {}) {
      const campaignStore = useCampaignStore()
      const campaignId = campaignStore.getActiveCampaign ? campaignStore.getActiveCampaign.id : 'pg-youtil-ro'
      const utmSource = routeQueries.utm_source ? routeQueries.utm_source : ''
      const utmMedium = routeQueries.utm_medium ? routeQueries.utm_medium : ''
      const utmCampaign = routeQueries.utm_campaign ? routeQueries.utm_campaign : ''
      if (this.customerToken && !campaignStore.getActiveCampaign) return this.customerToken

      try {
        const data = {
          campaignId,
          utmSource,
          utmMedium,
          utmCampaign
        }
        const resp = await api.post(`/users/init`, data)
        
        if (resp.data.response !== 'ERR') {
          this.customerToken = resp.data.customerToken
          return resp.data.customerToken
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp.data)
          throw resp.data
        }
      } catch (error) {
        const errorStore = useErrorStore()
        errorStore.handleError(error)
        throw error
      }
    },
    async uploadReceipt ({ reupload = false }) {
      try {
        this.isLoading = true
        const formData = new FormData()
        if (this.receiptToken) {
          formData.append('receiptToken', this.receiptToken)
        }
        formData.append('userToken', this.customerToken)
        const dev = import.meta.env.VITE_APP_ENV != 'production'
        console.log('dev', dev, import.meta.env.VITE_APP_ENV)
        const campaignStore = useCampaignStore()
        formData.append('subCampaignId', campaignStore.getActiveCampaign.id + (dev ? '-dev' : ''))
        formData.append('campaignId', reupload ? 'pg-ro-re' : 'pg-ro')
        formData.append('receipt', this.receipt.receiptImage, 'receipt.png')

        const resp = await axios.post(`${this.postbillUrl}`, formData)
        
        if (resp.data.response != 'ERR') {
          this.receiptToken = resp.data.receiptToken
          return resp.data.response
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp.data)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async uploadProductImage (data, token) {
      try {
        this.isLoading = true
        const formData = new FormData()
        formData.append('receiptToken', this.receiptToken)
        if (!token) formData.append('userToken', this.customerToken)
        const campaignStore = useCampaignStore()
        formData.append('campaignId', campaignStore.getActiveCampaign.id)
        formData.append('file', data, 'product.png')

        const url = token ? `/users/reuploadproductphoto?token=${token}` : `/users/productPhoto`
        const resp = await api.post(url, formData)
        
        if (resp.data.success) {
          return true
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async sendRetailerForm () {
      try {
        this.isLoading = true
        // ID is not needed in the request
        delete this.retailerForm.id
        const campaignStore = useCampaignStore()
        const formData = {
          ...this.retailerForm,
          campaignId: campaignStore.getActiveCampaign.id,
          customerToken: this.customerToken,
          receiptToken: this.receiptToken
        }

        const resp = await api.put(`/users/retailer`, formData)
        
        if (resp.data.success) {
          return true
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
        this.retailerForm = {}
      }
    },
    async lastStepCall () {
      try {
        this.isLoading = true
        const campaignStore = useCampaignStore()
        const formData = {
          campaignId: campaignStore.getActiveCampaign.id,
          customerToken: this.customerToken,
          receiptToken: this.receiptToken,
        }

        const resp = await api.put(`/users/lastStep`, formData)
        
        if (resp.data.success) {
          this.referenceNumber = resp.data.referenceNumber
          return true
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    setCustomerToken (token) {
      this.customerToken = token
    },
    parseJwt (token) {
      try {
        const base64Url = token.split('.')[1]; // Get the payload part of the token
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // Convert Base64Url to Base64
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
      } catch (e) {
        console.error('Invalid token:', e);
        return null
      }
    },
    async submitIban (data) {
      try {
        this.isLoading = true
        const campaignStore = useCampaignStore()
        const formData = {
          ...data,
          campaignId: campaignStore.getActiveCampaign.id,
          customerToken: this.customerToken,
        }

        const resp = await api.post(`/users/iban`, formData)
        
        if (resp.data.success) {
          return true
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async check72hrs (token) {
      try {
        const campaignStore = useCampaignStore()
        const formData = {
          token,
          receiptToken: this.receiptToken,
          campaignId: campaignStore.getActiveCampaign.id,
        }

        const resp = await api.post(`/users/check72hours`, formData)
        
        if (resp.data.success) {
          return true
        } else {
          throw new Error(resp.data.message || 'Check 72hrs failed')
        }
      } catch (err) {
        throw err instanceof Error ? err : new Error(err?.message || 'Check 72hrs failed')
      }
    },
    async ibanUpdateCheck () {
      try {
        const resp = await api.get(`/users/updateibancheck/${this.customerToken}`)
        
        if (resp.data.updateIban) {
          return true
        } else {
          throw new Error(resp.data.message || 'IBAN update check failed')
        }
      } catch (err) {
        throw err instanceof Error ? err : new Error(err?.message || 'IBAN update check failed')
      }
    },
    async updateIban (data) {
      try {
        this.isLoading = true
        const campaignStore = useCampaignStore()
        const formData = {
          ...data,
          campaignId: campaignStore.getActiveCampaign.id,
          customerToken: this.customerToken,
        }

        const resp = await api.put(`/users/iban`, formData)
        
        if (resp.data.success) {
          return true
        } else {
          const errorStore = useErrorStore()
          errorStore.handleError(resp)
          throw resp.data
        }
      } catch (err) {
        const errorStore = useErrorStore()
        errorStore.handleError(err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    setRetailer (data) {
      this.retailerForm = {
        ...this.retailerForm,
        ...data
      };
    }
  }
})
